import { useCallback, useMemo, useRef } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { STAY_PROPERTY_TYPES } from "@majourneys/shared";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../lib/currency";
import { useGuestNationality } from "../../../lib/guest-nationality";
import { useSession } from "../../auth/hooks/use-session";
import { hasBrowserSession } from "../../../lib/auth";
import {
  staysApi,
  type StaysCityResponse,
  type StayPricingParams,
  type StartBookingInput,
} from "./stays.api";

/**
 * Query keys. `lang` is part of every content key — a key missing it serves
 * stale (wrong-locale) content out of the cache after a locale switch, which
 * has bitten this codebase before (see `activityKeys` in
 * ../../activities/constants/query-keys.ts, which carries the same lesson).
 *
 * `city`/`property` also fold in every pricing param individually (not a
 * single serialized blob) so two different date ranges — or a dated vs.
 * dateless call for the same slug — never collide on the same cache entry.
 * `null` (not `undefined`) marks "no pricing requested" so a partial-vs-full
 * date set can never accidentally produce the same key by both stringifying
 * to `undefined`.
 */
/**
 * The children part of a key, with BOTH spellings of "no children" collapsed
 * to the same value.
 *
 * `[].join(",")` is `""` while `undefined?.join()` is `undefined`, so a plain
 * `childAges?.join(",") ?? null` produced two different keys for the same
 * search depending on which caller you asked. The route loaders pass
 * `undefined`; the pages pass `[]` straight out of `parseChildAges`.
 *
 * That mismatch made every stays page server-render its LOADING state. The
 * loader warmed the cache on the server, the component then asked for a
 * different key, missed, and rendered "Loading this stay..." into the HTML.
 * Measured 2026-08-14: crawlers got 611 characters of shell on every property
 * page and on /category/hotel, against 4,301 characters on an activity page.
 * The whole stays section was invisible to search.
 *
 * Normalising here rather than at the call sites is deliberate: there are
 * three key builders and many callers, and any one of them spelling emptiness
 * the other way would silently reintroduce it.
 */
function childAgesKey(childAges?: number[]): string | null {
  return childAges && childAges.length > 0 ? childAges.join(",") : null;
}

/**
 * The currency part of a key, deliberately null on an undated request.
 *
 * Currency only changes a response that actually carries a supplier quote
 * (see `buildStayPricingQuery`, which does not send it otherwise). Folding it
 * in unconditionally would give the same undated content four cache entries
 * and make a header currency switch refetch every stay page on the site for a
 * byte-identical body — and, worse, miss the loader-warmed entry on hydration
 * so every stays page server-renders its LOADING state again for any guest not
 * in MAD. That regression is documented at length in `childAgesKey` above.
 */
function currencyKey(pricing: StayPricingParams): string | null {
  const dated = !!pricing.checkin && !!pricing.checkout && pricing.adults != null;
  return dated ? (pricing.currency ?? "MAD") : null;
}

/**
 * The nationality part of a key, on exactly the same terms as `currencyKey`.
 *
 * It MUST be in the key. The supplier prices and gates inventory by market, so
 * two visitors in different countries can get different rooms and different
 * totals for one property and one date range. Left out, whoever asked first
 * would fill the cache entry and everyone else would read their market's
 * answer — the same class of bug as the currency one above, but quieter,
 * because a wrong price at least looks like a price.
 *
 * Null when undated, so an undated content page keeps ONE cache entry and
 * still hits the SSR-warmed one on hydration. That is not just an optimisation
 * here: the server render has no country to read (`/__geo` is client-only, by
 * design — see lib/guest-nationality.ts), so a nationality in an undated key
 * would miss the warmed entry on every visit and server-render the loading
 * state, which is the regression documented at length in `childAgesKey`.
 */
function nationalityKey(pricing: StayPricingParams): string | null {
  const dated = !!pricing.checkin && !!pricing.checkout && pricing.adults != null;
  return dated ? (pricing.guestNationality ?? "MA") : null;
}

export const stayKeys = {
  all: ["stays"] as const,
  destinations: (propertyType?: string, lang = "en") =>
    [...stayKeys.all, "destinations", propertyType ?? "all", lang] as const,
  /** `viewer`: as on `property`. Cards are member priced for a signed-in guest too. */
  city: (citySlug: string, lang: string, pricing: StayPricingParams = {}, viewer: string | null = null) =>
    [
      ...stayKeys.all,
      "city",
      citySlug,
      lang,
      pricing.propertyType ?? null,
      pricing.checkin ?? null,
      pricing.checkout ?? null,
      pricing.adults ?? null,
      childAgesKey(pricing.childAges),
      currencyKey(pricing),
      nationalityKey(pricing),
      viewer,
    ] as const,
  /**
   * Deliberately NOT keyed on lang or on the guest's dates: the calendar is
   * about which nights exist, which is the same answer in every language and
   * does not change because the guest moved their range. Keying it on the
   * dates would refetch sixty supplier calls on every date click.
   */
  calendar: (slug: string, adults: number, currency: string) =>
    [...stayKeys.all, "calendar", slug, adults, currency] as const,
  /**
   * `viewer` is the signed-in user id the rooms were priced for (member
   * prices), or null. It defaults to null so route loaders, which run on the
   * server where there is no session, build the same key as the component's
   * first render.
   */
  property: (
    slug: string,
    lang: string,
    pricing: StayPricingParams = {},
    viewer: string | null = null,
  ) =>
    [
      ...stayKeys.all,
      "property",
      slug,
      lang,
      pricing.propertyType ?? null,
      pricing.checkin ?? null,
      pricing.checkout ?? null,
      pricing.adults ?? null,
      childAgesKey(pricing.childAges),
      currencyKey(pricing),
      nationalityKey(pricing),
      viewer,
    ] as const,
  booking: (reference: string) => [...stayKeys.all, "booking", reference] as const,
  offer: (offerId: string) => [...stayKeys.all, "offer", offerId] as const,
  type: (propertyType: string, lang: string, pricing: StayPricingParams = {}, viewer: string | null = null) =>
    [
      ...stayKeys.all,
      "type",
      propertyType,
      lang,
      pricing.checkin ?? null,
      pricing.checkout ?? null,
      pricing.adults ?? null,
      childAgesKey(pricing.childAges),
      currencyKey(pricing),
      nationalityKey(pricing),
      viewer,
    ] as const,
  cancelQuote: (reference: string) => [...stayKeys.all, "booking", reference, "cancel-quote"] as const,
};

/**
 * Folds the guest's header currency into whatever pricing params a caller
 * passed.
 *
 * Done here, in the hooks, rather than at each page: there are four call sites
 * across three pages and a checkout, and a page that forgot to pass it would
 * not break — it would quietly quote MAD to a guest browsing in USD, which is
 * exactly the bug this is fixing and exactly the kind that goes unnoticed.
 *
 * An explicit `pricing.currency` still wins, so a caller that genuinely needs
 * a fixed currency can say so.
 *
 * Route LOADERS deliberately do not use this: they run during SSR, where the
 * guest's choice (localStorage, plus the /__geo hint) does not exist yet. They
 * warm the default, and the client re-queries only when the guest is both in a
 * non-default currency AND has picked dates — see `currencyKey`.
 */
function usePricingWithGuestContext(pricing: StayPricingParams): StayPricingParams {
  const { currency } = useCurrency();
  /**
   * Undefined until the geo probe answers, and undefined for ever for a
   * Moroccan visitor or one we cannot place. That is deliberate: the params
   * then match what a route loader built, so the first render reads the
   * SSR-warmed cache entry instead of firing a second paid supplier search
   * for the same room.
   */
  const guestNationality = useGuestNationality();
  return useMemo(
    () => ({
      ...pricing,
      currency: pricing.currency ?? currency,
      guestNationality: pricing.guestNationality ?? guestNationality,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currency,
      guestNationality,
      pricing.checkin,
      pricing.checkout,
      pricing.adults,
      pricing.propertyType,
      pricing.currency,
      pricing.guestNationality,
      childAgesKey(pricing.childAges),
    ],
  );
}

/** Destination list is editorial content (curated cities) — cache it longer than a price. */
const CONTENT_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * City/property responses carry a `price` (or `rooms`) field whenever dates
 * are supplied. LiteAPI rates move independently of our own cache and we
 * never want to show a guest a total that's gone stale by the time they
 * check out, so this is capped at 60s regardless of whether pricing was
 * actually requested on a given call — simpler than branching staleTime on
 * "did this call include dates" for a saving that isn't worth the
 * conditional.
 */
const PRICE_STALE_TIME_MS = 60 * 1000;

/**
 * A cancellation quote's `chargeAmount`/`refundAmount` are computed against
 * "now" on the server (see `chargeAtInstant` in
 * apps/api/src/features/stays/utility/cancellation.ts) — the free-cancellation
 * cutoff can pass between renders, so this is never cached across calls.
 */
const CANCEL_QUOTE_STALE_TIME_MS = 0;

/**
 * `propertyType` narrows the list to cities that hold that kind of stay.
 *
 * Called with nothing it still answers "which cities have any stay at all",
 * which is what the subcategory chips need to decide whether to render.
 */
export function useStayDestinations(propertyType?: string) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  return useQuery({
    queryKey: stayKeys.destinations(propertyType, lang),
    queryFn: () => staysApi.destinations(propertyType, lang),
    staleTime: CONTENT_STALE_TIME_MS,
  });
}

/**
 * Route-loader options, so the server can fetch BEFORE it renders.
 *
 * Without these the SSR pass renders the loading branch and ships
 * "Loading stays..." to the crawler, which defeats the entire reason these
 * pages carry our own writing. Verified in a browser on 2026-08-13: the
 * property page's server HTML contained no property name at all.
 */
export function stayCityQueryOptions(
  citySlug: string,
  lang: string,
  pricing: StayPricingParams = {},
  viewer: string | null = null,
) {
  return {
    queryKey: stayKeys.city(citySlug, lang, pricing, viewer),
    // Only a member-keyed entry may carry the cookie; see `anonymous` on staysApi.property.
    queryFn: () => staysApi.city(citySlug, lang, pricing, { anonymous: viewer === null }),
    staleTime: PRICE_STALE_TIME_MS,
  };
}

/** Every property of one type, across every city that has hotels. */
export interface StaysTypeResult {
  propertyType: string;
  properties: StaysCityResponse["properties"];
  /** True when ANY city could not be priced, so the page can say so once. */
  pricingUnavailable: boolean;
}

/** Every property type at once: the Stays hub's "All". */
export const ALL_STAY_TYPES = "all";

/**
 * `/stays/all`, or the old one-request-per-city chain if the API answering
 * does not have that route yet. The web and the API deploy separately, so for
 * a minute or two after a release the page can be newer than the API.
 */
async function allOrPerCity(lang: string, pricing: StayPricingParams, anonymous: boolean) {
  try {
    return await staysApi.all(lang, pricing, { anonymous });
  } catch {
    const { destinations } = await staysApi.destinations(pricing.propertyType);
    const perCity = await Promise.all(
      destinations
        .filter((d) => d.slug && (d.propertyCount ?? 0) > 0)
        .map((d) => staysApi.city(d.slug as string, lang, pricing, { anonymous })),
    );
    return {
      properties: perCity.flatMap((r) => r.properties),
      pricingUnavailable: perCity.some((r) => r.pricingUnavailable),
    };
  }
}

async function fetchStaysByType(
  propertyType: string,
  lang: string,
  pricing: StayPricingParams,
  citySlug?: string,
  anonymous = true,
): Promise<StaysTypeResult> {
  const type = propertyType === ALL_STAY_TYPES ? undefined : propertyType;
  const scoped = { ...pricing, propertyType: type };
  // One request either way: the city's own list when the page is narrowed to
  // a city, otherwise every city at once. The old chain (which cities hold
  // this type, then each of them) kept the Stays hub on camps only for up to
  // 20 seconds on a phone (owner, 2026-09-26).
  const result = citySlug
    ? await staysApi.city(citySlug, lang, scoped, { anonymous })
    : await allOrPerCity(lang, scoped, anonymous);
  return {
    propertyType,
    properties: result.properties,
    pricingUnavailable: result.pricingUnavailable,
  };
}

/**
 * `citySlug` narrows to one city's own list (the city Stays hubs). The key is
 * built here and only here, so a route loader warming a city hub and the
 * page reading it can never drift apart and run the supplier lookup twice.
 */
export function stayTypeQueryOptions(
  propertyType: string,
  lang: string,
  pricing: StayPricingParams = {},
  citySlug?: string,
  viewer: string | null = null,
) {
  const key = stayKeys.type(propertyType, lang, pricing, viewer);
  return {
    queryKey: citySlug ? ([...key, citySlug] as const) : key,
    queryFn: () => fetchStaysByType(propertyType, lang, pricing, citySlug, viewer === null),
    staleTime: PRICE_STALE_TIME_MS,
  };
}

export function useStaysByType(
  propertyType: string,
  pricingInput: StayPricingParams = {},
  citySlug?: string,
) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const pricing = usePricingWithGuestContext(pricingInput);
  const viewer = usePricingViewer(pricing);
  return useQuery({
    // The route loaders warm these same options, so the page reads what the
    // server already fetched.
    ...stayTypeQueryOptions(propertyType, lang, pricing, citySlug, viewer),
    enabled: !!propertyType,
  });
}

export function stayPropertyQueryOptions(slug: string, lang: string, pricing: StayPricingParams = {}) {
  return {
    // The key's viewer is null, so the fetch must be anonymous to match it.
    queryKey: stayKeys.property(slug, lang, pricing),
    queryFn: () => staysApi.property(slug, lang, pricing, { anonymous: true }),
    staleTime: PRICE_STALE_TIME_MS,
  };
}

export function useStayCity(citySlug: string, pricingInput: StayPricingParams = {}) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const pricing = usePricingWithGuestContext(pricingInput);
  const viewer = usePricingViewer(pricing);
  return useQuery({
    ...stayCityQueryOptions(citySlug, lang, pricing, viewer),
    enabled: !!citySlug,
    staleTime: PRICE_STALE_TIME_MS,
    // No `keepPreviousData`: this hook re-keys on every pricing param
    // (see stayKeys.city above), so a date change is a brand-new cache
    // entry rather than an update to the current one. Keeping the previous
    // entry's data visible while the new one loads would mean showing last
    // week's prices for this week's dates — silently wrong, not just stale.
  });
}

export function useStayProperty(slug: string, pricingInput: StayPricingParams = {}) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const pricing = usePricingWithGuestContext(pricingInput);
  /**
   * Who the rooms were priced for. Null on the server (no session there) and
   * for signed-out guests, so the first render reads the SSR-warmed public
   * entry. Once the browser knows the user, the key changes and the rooms
   * refetch with the cookie at the member price. Undated pages carry no
   * price, so they keep one entry for everyone.
   */
  const viewer = usePricingViewer(pricing);
  const sessionSettling = useSessionSettling();
  const dated = !!pricing.checkin && !!pricing.checkout && pricing.adults != null;
  const query = useQuery({
    queryKey: stayKeys.property(slug, lang, pricing, viewer),
    // Only a member-keyed entry may carry the cookie; see `anonymous`.
    queryFn: () => staysApi.property(slug, lang, pricing, { anonymous: viewer === null }),
    enabled: !!slug,
    staleTime: PRICE_STALE_TIME_MS,
    /**
     * Previous data is held ONLY to keep the page mounted through a re-price.
     * Without it the detail page fell back to its loading branch on every date
     * or guest change, which unmounted the booking widget and threw away what
     * the guest was doing: the calendar shut after the first date, and the
     * guests panel shut before an age could be chosen.
     *
     * The old rule still holds and is now enforced at the render site instead:
     * callers must read `isPlaceholderData` and refuse to show rooms or a
     * total while it is true, because those belong to the PREVIOUS dates. The
     * name, photos and description do not change with dates, so they are safe
     * to keep on screen.
     */
    placeholderData: keepPreviousData,
  });

  /** Who the data on screen was priced for, once it settled. */
  const settledViewer = useRef<string | null | undefined>(undefined);
  if (query.data && !query.isPlaceholderData) settledViewer.current = viewer;

  return {
    ...query,
    /**
     * True while the price this page shows may still move for a reason other
     * than the guest changing dates: the session is still being read, so a
     * public price could be swapped for a member one. Callers show the price
     * loading state rather than a number. Riad 7 Saints, traveller test
     * 2026-11-01: a signed-in guest saw "949 × 4", then the "from" 895 in the
     * bar, then 813 once the member rooms landed.
     */
    pricePending: dated && sessionSettling,
    /**
     * The held (placeholder) rooms were priced for a different viewer: a
     * public total sitting under a member re-price. Not worth keeping on
     * screen dimmed; it is a different price, not an older one.
     */
    heldForOtherViewer: query.isPlaceholderData && settledViewer.current !== viewer,
  };
}

/**
 * Whose price a dated stay request is for: the signed-in user's id, or null.
 * Null on the server and for signed-out guests, so the first render reads the
 * SSR-warmed public entry.
 */
function usePricingViewer(pricing: StayPricingParams): string | null {
  const { data: session } = useSession();
  const dated = !!pricing.checkin && !!pricing.checkout && pricing.adults != null;
  return dated ? (session?.user?.id ?? null) : null;
}

/**
 * True while the browser has a session cookie but has not read the session
 * yet, so `usePricingViewer` may still change from null to a user id.
 * Signed-out guests (no cookie hint) never wait.
 */
function useSessionSettling(): boolean {
  const { isPending } = useSession();
  const ctx = useRouteContext({ strict: false }) as { hasSessionHint?: boolean } | undefined;
  if (!isPending) return false;
  return !!ctx?.hasSessionHint || hasBrowserSession();
}

/**
 * The exact offer the guest picked, resolved from the id in the checkout URL.
 *
 * This is what checkout renders from. It deliberately does NOT re-search:
 * the supplier rewords room names between identical searches, so a re-search
 * regularly failed to find the room the guest had just clicked and left them
 * on "we could not find this room". The price actually charged still comes
 * from the supplier at prebook, never from this snapshot.
 */
export function useServedOffer(offerId: string | undefined) {
  return useQuery({
    queryKey: stayKeys.offer(offerId ?? ""),
    queryFn: () => staysApi.servedOffer(offerId!),
    enabled: !!offerId,
    // The server forgets an offer after ten minutes, so there is no value in
    // holding it here for longer than the guest's own visit.
    staleTime: PRICE_STALE_TIME_MS,
    retry: false,
  });
}

export function useStayBooking(reference: string) {
  return useQuery({
    queryKey: stayKeys.booking(reference),
    queryFn: () => staysApi.getBooking(reference),
    enabled: !!reference,
    staleTime: PRICE_STALE_TIME_MS,
  });
}

/**
 * One-shot imperative read of a booking row, for code that is inside a
 * callback rather than a render.
 *
 * The confirmation page needs this to tell two very different things apart:
 * `finaliseBooking` REJECTS both for a transient blip and for a booking the
 * server has already given up on (it answers 409 for a `failed`/`cancelled`
 * row, see stays.booking.service.ts), and the page cannot ask the guest to
 * wait forever on the strength of an error it cannot classify.
 *
 * `fetchQuery` rather than `useQuery` because the read is conditional on a
 * failure that may never happen, and `staleTime: 0` because the whole point
 * is the row's status RIGHT NOW: a cached copy from the mutation's own
 * `onSuccess` would answer with the state we already knew about.
 */
export function useReadStayBooking() {
  const queryClient = useQueryClient();
  return useCallback(
    (reference: string) =>
      queryClient.fetchQuery({
        queryKey: stayKeys.booking(reference),
        queryFn: () => staysApi.getBooking(reference),
        staleTime: 0,
      }),
    [queryClient],
  );
}

export function useStayCancelQuote(reference: string, enabled = true) {
  return useQuery({
    queryKey: stayKeys.cancelQuote(reference),
    queryFn: () => staysApi.cancelQuote(reference),
    enabled: enabled && !!reference,
    staleTime: CANCEL_QUOTE_STALE_TIME_MS,
  });
}

export function useStartStayBooking() {
  return useMutation({
    mutationKey: [...stayKeys.all, "start-booking"] as const,
    mutationFn: (input: StartBookingInput) => staysApi.startBooking(input),
  });
}

export function useFinaliseStayBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...stayKeys.all, "finalise-booking"] as const,
    mutationFn: (reference: string) => staysApi.finaliseBooking(reference),
    onSuccess: (booking) => {
      queryClient.setQueryData(stayKeys.booking(booking.reference), booking);
    },
  });
}

export function useCancelStayBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...stayKeys.all, "cancel-booking"] as const,
    mutationFn: ({ reference, email }: { reference: string; email: string }) =>
      staysApi.cancelBooking(reference, email),
    onSuccess: (_result, { reference }) => {
      queryClient.invalidateQueries({ queryKey: stayKeys.booking(reference) });
    },
  });
}

/**
 * Which nights this property can be booked on, asked live when the guest opens
 * it.
 *
 * Sixty supplier calls sounds like a lot until you compare it with what it
 * replaces: a background sweep of the whole catalogue, ~50,000 calls every six
 * hours, which took longer than the gap between its own runs and so never
 * completed. A guest only ever looks at one property.
 *
 * Five minutes of staleness is the deliberate ceiling — long enough that
 * flicking between months is free, short enough that it stays true.
 */
export function useStayCalendar(
  slug: string,
  options: { adults?: number; enabled?: boolean } = {},
) {
  const { currency } = useCurrency();
  const adults = options.adults ?? 2;
  return useQuery({
    queryKey: stayKeys.calendar(slug, adults, currency),
    queryFn: () => staysApi.propertyCalendar(slug, { adults, currency }),
    enabled: !!slug && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * How many published properties each hotel type has, in the city when there
 * is one. Counted from the same undated "All" list the hub loads, so the type
 * tiles arrive with the cards in one request instead of five more.
 */
export function useStayTypeCounts(enabled: boolean, citySlug?: string): Map<string, number> | null {
  const { i18n } = useTranslation();
  // No currency or nationality in the key: counts do not depend on them, and
  // this way it reads exactly what the route loader warmed on the server.
  const lang = i18n.language;
  const { data } = useQuery({ ...stayTypeQueryOptions(ALL_STAY_TYPES, lang, {}, citySlug), enabled });
  if (!enabled || !data) return null;
  const counts = new Map<string, number>(STAY_PROPERTY_TYPES.map((type) => [type, 0]));
  for (const p of data.properties) {
    if (counts.has(p.propertyType)) counts.set(p.propertyType, (counts.get(p.propertyType) ?? 0) + 1);
  }
  return counts;
}
